import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import { connect } from 'react-redux';

import { ILogger, CueLogger, setToLocalStorage } from '@cuemath/intel';

import { post as addSHCData } from 'actions/add-health-check-data';
import { showNotification } from 'actions/notification';

import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';
import { getPingStatus, getSpeedStatus } from '../../utils/speed-test';

const defaultStates = {
  internetProcessing: false,
  shcDataPost: store.post(),
  speedAnalysedData: {},
  allTestResult: {},
  speedTestCompleted: false,
};

const SLOW = 'Slow';
let dailySpeedTestTimerId = null;
const SPEED_TEST_START_TIMEOUT = 10000;

class SpeedTest extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...defaultStates,
    };
  }

  componentDidMount() {
    dailySpeedTestTimerId = setTimeout(() => {
      this.startSpeedTest();
    }, SPEED_TEST_START_TIMEOUT);
  }

  componentWillUnmount() {
    if (dailySpeedTestTimerId) {
      clearTimeout(dailySpeedTestTimerId);
    }
  }

  startSpeedTest = () => {
    this.setState({ internetProcessing: true });
    window.addEventListener('message', this.speedTestListener);
    setTimeout(this.timeoutLog, 2000);
  };

  timeoutLog = () => {
    const { speedTestCompleted } = this.state;
    const { userId } = this.props;

    if (!speedTestCompleted) {
      trackEvent(EVENT_TYPE.BACKGROUND_INTERNET_SPEED_TEST_TIMEOUT, {
        level: ILogger.LOG_LEVELS.INFO,
        user_id: userId,
      });
    }
  };

  speedTestListener = event => {
    if (event.origin !== 'https://cuemath-leap.speedtestcustom.com') {
      return;
    }

    CueLogger.print('Background Speed Data ', event);
    this.analyseEventData(event.data);
  };

  analyseEventData = data => {
    const { download, upload, latency } = data || {};
    const { minimum } = latency || {};

    const downloadSpeedValue = Number(download / 1000).toFixed(2);
    const uploadSpeedValue = Number(upload / 1000).toFixed(2);
    const pingValue = minimum;

    const pingStatus = getPingStatus(pingValue);
    const downloadSpeedStatus = getSpeedStatus(downloadSpeedValue);
    const uploadSpeedStatus = getSpeedStatus(uploadSpeedValue);

    const analysedData = {
      downloadSpeedStatus,
      downloadSpeedValue,
      pingStatus,
      pingValue,
      uploadSpeedStatus,
      uploadSpeedValue,
    };

    this.onUpdateTestState(data, analysedData);
  };

  onUpdateTestState = (eventData, analysedData) => {
    const { downloadSpeedStatus, uploadSpeedStatus, pingStatus } = analysedData;

    const isInternetSlow =
      downloadSpeedStatus === SLOW || uploadSpeedStatus === SLOW || pingStatus === SLOW;

    this.onLogEvent({
      finalResult: !isInternetSlow,
      test: 'Speed',
      ...analysedData,
    });

    const speedTestData = {
      eventData,
      finalState: !isInternetSlow,
      download: downloadSpeedStatus,
      ping: pingStatus,
      upload: uploadSpeedStatus,
    };

    this.setState(
      {
        speedTestCompleted: true,
        allTestResult: { speed: speedTestData },
      },
      () => {
        this.handleFinalTestResult(analysedData);
      },
    );
  };

  decideText = analysedData => {
    const {
      downloadSpeedStatus,
      uploadSpeedStatus,
      downloadSpeedValue,
      uploadSpeedValue,
      pingValue,
      pingStatus,
    } = analysedData;

    const isUploadSlow = uploadSpeedStatus === SLOW;
    const isDownloadSlow = downloadSpeedStatus === SLOW;
    const isPingSlow = pingStatus === SLOW;

    const UPGRADE_INTERNET_MSG =
      'Please upgrade your internet connection to meet the recommended speed and ping values in order to continue taking Cuemath classes.\n';

    const uploadReport = `The recommended Upload speed is ≥ 8 Mbps. Your speed is ${uploadSpeedValue} Mbps.\n`;

    const downloadReport = `The recommended Download speed is ≥ 8 Mbps. Your speed is ${downloadSpeedValue} Mbps.\n`;

    const pingReport = `The recommended ping value is ≤ 10 ms. Your ping is ${pingValue} ms.\n`;

    const resolution =
      isUploadSlow || isDownloadSlow || isPingSlow ? UPGRADE_INTERNET_MSG : '';

    return uploadReport + downloadReport + pingReport + resolution;
  };

  handleFinalTestResult = analysedData => {
    const { actions, userId, isSpeedTestDone, teacherClassroomId } = this.props;
    const { allTestResult, shcDataPost } = this.state;

    if (shcDataPost.current && shcDataPost.current.isCreated) {
      return;
    }

    if (allTestResult) {
      actions.addSHCData(shcDataPost.id, {
        user_id: userId,
        user_type: 'ACADEMIC_COUNSELOR',
        classroom_id: teacherClassroomId,
        meta: allTestResult,
      });
    }
    this.setState({ ...defaultStates });
    window.removeEventListener('message', this.speedTestListener);

    setToLocalStorage(`TeacherClassroomIdSpeedTestDone`, teacherClassroomId);

    isSpeedTestDone(true);

    actions.showNotification({
      text: this.decideText(analysedData),
      duration: 10,
      showCloseIcon: true,
    });
  };

  onLogEvent = data => {
    const { userId, teacherClassroomId } = this.props;

    trackEvent(EVENT_TYPE.BACKGROUND_INTERNET_SPEED_TEST, {
      ...data,
      level: ILogger.LOG_LEVELS.INFO,
      user_id: userId,
      classroom_id: teacherClassroomId,
    });
  };

  render() {
    const { userId } = this.props;
    const { internetProcessing } = this.state;

    if (!userId) {
      return null;
    }

    return (
      <>
        {internetProcessing && (
          <iframe
            name="backgroundInternetSpeedTest"
            title="Cuemath"
            height="0px"
            width="0px"
            frameBorder="1"
            src="https://cuemath-leap.speedtestcustom.com"
            style={{ display: 'block' }}
          />
        )}
      </>
    );
  }
}

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      addSHCData,
      showNotification,
    },
    dispatch,
  ),
}))(SpeedTest);

SpeedTest.propTypes = {
  actions: PropTypes.shape({}).isRequired,
};
